import axios from "axios";
import { getTempUserToken, getUserToken } from "@/services/storage-data";
import { createFilePreviewFile } from "@/widgets/file-uploader-widget/create-file-preview";

export const CLOUD_FILES_IL_API_ADDRESS =
  "https://gomake-cloud-files-api-il-683372436778.me-west1.run.app";

export const CLOUD_FILES_EUROPE_WEST3_API_ADDRESS =
  "https://gomake-cloud-files-api-1054639123520.europe-west3.run.app";

export const CLOUD_FILES_EUROPE_WEST3_STAGING_API_ADDRESS =
  "https://gomake-cloud-file-api-386717556173.europe-west3.run.app";

/** Minimum allowed upload size (1 KB). */
export const MIN_UPLOAD_FILE_SIZE_BYTES = 1024;

/** Maximum allowed upload size (1 GB). */
export const MAX_UPLOAD_FILE_SIZE_BYTES = 1024 * 1024 * 1024;

/** Allowed file extensions for CreateUploadSession (lowercase, including leading dot). */
export const ALLOWED_UPLOAD_FILE_EXTENSIONS = [
  ".xml",
  ".png",
  ".pdf",
  ".json",
  ".jpeg",
  ".dxf",
  ".jpg",
  ".msg",
  ".psb",
  ".eps",
  ".psd",
  ".webp",
  ".ai",
  ".zip",
  ".xlsx",
  ".jfif",
  ".csv",
  ".rar",
  ".docx",
  ".doc",
  ".pptx",
  ".ini",
  ".xls",
  ".indd",
  ".gif",
  ".ds_store",
  ".mp4",
  ".ps",
  ".svg",
  ".txt",
  ".bmp",
  ".ttf",
  ".tiff",
  ".indt",
  ".heic",
  ".ogg",
  ".jdf",
  ".orf",
  ".eml",
  ".brg",
  ".tif",
  ".avif",
  ".7z",
  ".mov",
  ".lnk",
] as const;

const ALLOWED_UPLOAD_FILE_EXTENSIONS_SET = new Set<string>(
  ALLOWED_UPLOAD_FILE_EXTENSIONS,
);

const MISCELLANEOUS_FILES_API_ADDRESSES = [
  CLOUD_FILES_IL_API_ADDRESS,
  "https://gomake-cloud-files-api-683372436778.europe-west9.run.app",
  CLOUD_FILES_EUROPE_WEST3_STAGING_API_ADDRESS,
  CLOUD_FILES_EUROPE_WEST3_API_ADDRESS,
];

const DIRECT_GCS_UPLOAD_ADDRESSES = [
  CLOUD_FILES_IL_API_ADDRESS,
  CLOUD_FILES_EUROPE_WEST3_API_ADDRESS,
  "https://gomake-cloud-files-api-683372436778.europe-west9.run.app",
  CLOUD_FILES_EUROPE_WEST3_STAGING_API_ADDRESS,
];

export const isMiscellaneousFilesEnabled = (filesApiAddress?: string | null) =>
  MISCELLANEOUS_FILES_API_ADDRESSES.some(
    (address) => address === filesApiAddress?.trim(),
  );

export const isDirectGcsUploadFilesApi = (filesApiAddress?: string | null) =>
  DIRECT_GCS_UPLOAD_ADDRESSES.some(
    (address) => address === filesApiAddress?.trim(),
  );

export function resolveFilesApiAddress(
  ...candidates: Array<string | null | undefined>
): string {
  for (const candidate of candidates) {
    const trimmed = candidate?.trim();
    if (trimmed) {
      return trimmed.replace(/\/$/, "");
    }
  }
  throw new Error(
    "File upload is not configured for this print house (missing files API address).",
  );
}

export function getFilesApiAuthToken(): string {
  const token = getUserToken() || getTempUserToken();
  if (!token) {
    throw new Error("Authentication required to upload files.");
  }
  return token;
}

export function getFileExtension(fileName: string): string {
  const trimmed = fileName?.trim() ?? "";
  const lastDot = trimmed.lastIndexOf(".");
  if (lastDot <= 0 || lastDot === trimmed.length - 1) {
    // No extension, or leading-dot-only names like ".gitignore" still have an extension after the last dot.
    // Treat ".DS_Store" as extension ".ds_store" (lastDot === 0 is allowed).
    if (lastDot === 0 && trimmed.length > 1) {
      return trimmed.toLowerCase();
    }
    return "";
  }
  return trimmed.slice(lastDot).toLowerCase();
}

/**
 * Validates file extension and size before CreateUploadSession.
 * Returns a user-facing error message, or null when the file is allowed.
 */
export function validateFileForUpload(file: File): string | null {
  if (!file) {
    return "File is required.";
  }

  const fileName = file.name?.trim();
  if (!fileName) {
    return "File name is required.";
  }

  const extension = getFileExtension(fileName);
  if (!extension || !ALLOWED_UPLOAD_FILE_EXTENSIONS_SET.has(extension)) {
    return `File type '${extension || "unknown"}' is not allowed.`;
  }

  if (file.size < MIN_UPLOAD_FILE_SIZE_BYTES) {
    return "File is too small. Minimum size is 1 KB.";
  }

  if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
    return "File is too large. Maximum size is 1 GB.";
  }

  return null;
}

export function assertFileAllowedForUpload(file: File): void {
  const error = validateFileForUpload(file);
  if (error) {
    throw new Error(error);
  }
}

export interface CreateUploadSessionResult {
  sessionUri: string;
  bucketName?: string;
  fileUrl: string;
}

export interface CreateUploadSessionRequestBody {
  fileName: string;
  contentType: string;
  fileSize: number;
  isDev?: boolean;
  origin?: string;
  bucketName?: string;
}

/** Browser origin for GCS resumable session CORS (any host, including the shared app). */
export function getBrowserOrigin(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  const origin = window.location?.origin;
  return origin?.trim() ? origin : null;
}

/**
 * Build CreateUploadSession body. Always pass the current browser origin so Cloud Files API
 * can bind the GCS resumable session to the site the employee is on (shared or print-house).
 * Also enforces extension + size validation for every upload entry point.
 */
export function buildCreateUploadSessionRequestBody(
  file: File,
  options?: { bucketName?: string },
): CreateUploadSessionRequestBody {
  assertFileAllowedForUpload(file);

  const body: CreateUploadSessionRequestBody = {
    fileName: file.name,
    contentType: file.type || "application/octet-stream",
    fileSize: file.size,
  };

  if (options?.bucketName?.trim()) {
    body.bucketName = options.bucketName.trim();
  }

  const origin = getBrowserOrigin();
  if (origin) {
    body.origin = origin;
  }

  return body;
}

/**
 * Validates generated data files (manifest JSON) for upload — waives the 1 KB minimum.
 */
export function validateGeneratedDataFileForUpload(file: File): string | null {
  if (!file) {
    return "File is required.";
  }

  const fileName = file.name?.trim();
  if (!fileName) {
    return "File name is required.";
  }

  const extension = getFileExtension(fileName);
  if (!extension || !ALLOWED_UPLOAD_FILE_EXTENSIONS_SET.has(extension)) {
    return `File type '${extension || "unknown"}' is not allowed.`;
  }

  if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
    return "File is too large. Maximum size is 1 GB.";
  }

  return null;
}

export function assertGeneratedDataFileAllowedForUpload(file: File): void {
  const error = validateGeneratedDataFileForUpload(file);
  if (error) {
    throw new Error(error);
  }
}

/**
 * Build CreateUploadSession body for small generated manifests (sub-1 KB allowed).
 */
export function buildGeneratedDataFileUploadSessionRequestBody(
  file: File,
  options?: { bucketName?: string },
): CreateUploadSessionRequestBody {
  assertGeneratedDataFileAllowedForUpload(file);

  const body: CreateUploadSessionRequestBody = {
    fileName: file.name,
    contentType: file.type || "application/octet-stream",
    fileSize: file.size,
  };

  if (options?.bucketName?.trim()) {
    body.bucketName = options.bucketName.trim();
  }
  return body;
}

export async function createCloudFilesGeneratedDataUploadSession(
  filesApiAddress: string,
  file: File,
): Promise<CreateUploadSessionResult> {
  assertGeneratedDataFileAllowedForUpload(file);

  const apiBase = resolveFilesApiAddress(filesApiAddress);
  const token = getFilesApiAuthToken();

  try {
    const response = await axios.post(
      `${apiBase}/api/Files/CreateUploadSession`,
      buildGeneratedDataFileUploadSessionRequestBody(file),
      {
        headers: {
          authorization: "Bearer " + token,
        },
      },
    );

    const { sessionUri, bucketName: responseBucket, fileUrl } = response.data || {};
    if (!sessionUri || !fileUrl) {
      throw new Error("Failed to start file upload session.");
    }

    return {
      sessionUri,
      bucketName: responseBucket,
      fileUrl,
    };
  } catch (error: any) {
    const status = error?.response?.status;
    const serverMessage =
      error?.response?.data?.message ||
      error?.response?.data?.title ||
      error?.response?.data;

    if (status) {
      throw new Error(
        `CreateUploadSession failed (${status})${
          serverMessage ? `: ${JSON.stringify(serverMessage)}` : ""
        }`,
      );
    }
    throw error;
  }
}

function readUploadSessionErrorMessage(data: unknown): string {
  if (typeof data === "string" && data.trim()) {
    return data.trim();
  }
  if (data && typeof data === "object") {
    const record = data as { message?: unknown; title?: unknown };
    if (typeof record.message === "string" && record.message.trim()) {
      return record.message.trim();
    }
    if (typeof record.title === "string" && record.title.trim()) {
      return record.title.trim();
    }
  }
  return "";
}

export async function createCloudFilesUploadSession(
  filesApiAddress: string,
  file: File,
  options?: { bucketName?: string },
): Promise<CreateUploadSessionResult> {
  assertFileAllowedForUpload(file);

  const apiBase = resolveFilesApiAddress(filesApiAddress);
  const token = getFilesApiAuthToken();

  try {
    const response = await axios.post(
      `${apiBase}/api/Files/CreateUploadSession`,
      buildCreateUploadSessionRequestBody(file, options),
      {
        headers: {
          authorization: "Bearer " + token,
        },
      },
    );

    const { sessionUri, bucketName: responseBucket, fileUrl } = response.data || {};
    if (!sessionUri || !fileUrl) {
      throw new Error("Failed to start file upload session.");
    }

    return {
      sessionUri,
      bucketName: responseBucket,
      fileUrl,
    };
  } catch (error: any) {
    const status = error?.response?.status;
    const serverMessage = readUploadSessionErrorMessage(error?.response?.data);

    if (status) {
      throw new Error(
        serverMessage || `Failed to start file upload session (${status}).`,
      );
    }
    throw error;
  }
}

export async function createCloudFilesDownloadUrl(
  filesApiAddress: string,
  bucketKey: string,
): Promise<string> {
  const apiBase = resolveFilesApiAddress(filesApiAddress);
  const token = getFilesApiAuthToken();
  const trimmedKey = bucketKey?.trim();

  if (!trimmedKey) {
    throw new Error("Missing bucket key for download URL.");
  }

  try {
    const response = await axios.post(
      `${apiBase}/api/Files/CreateDownloadFilePreSignedUrl`,
      { bucketKey: trimmedKey },
      {
        headers: {
          authorization: "Bearer " + token,
        },
      },
    );

    const downloadUrl =
      typeof response.data === "string"
        ? response.data.trim()
        : typeof response.data?.url === "string"
          ? response.data.url.trim()
          : "";

    if (!downloadUrl) {
      throw new Error("Failed to create download URL for uploaded file.");
    }

    return downloadUrl;
  } catch (error: any) {
    const status = error?.response?.status;
    const serverMessage =
      error?.response?.data?.message ||
      error?.response?.data?.title ||
      error?.response?.data;

    if (status) {
      throw new Error(
        `CreateDownloadFilePreSignedUrl failed (${status})${
          serverMessage ? `: ${JSON.stringify(serverMessage)}` : ""
        }`,
      );
    }
    throw error;
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function readScreenshotUrl(data: any): string {
  const url =
    (typeof data?.screenshotUrl === "string" && data.screenshotUrl) ||
    (typeof data?.ScreenshotUrl === "string" && data.ScreenshotUrl) ||
    "";
  return url.trim();
}

async function uploadScreenshotViaBase64(
  apiBase: string,
  token: string,
  previewFile: File,
): Promise<string> {
  const base64Content = await blobToDataUrl(previewFile);
  const response = await axios.post(
    `${apiBase}/api/Files/UploadBase64`,
    {
      quoteItemId: "00000000-0000-0000-0000-000000000000",
      fileName: previewFile.name,
      base64Content,
    },
    {
      headers: {
        authorization: "Bearer " + token,
      },
    },
  );
  return readScreenshotUrl(response.data);
}

/**
 * Uploads a browser-generated JPEG preview and returns a public screenshot URL.
 * Prefers UploadScreenshot (S3, no Ghostscript). Falls back to UploadBase64.
 */
export async function uploadClientScreenshot(
  filesApiAddress: string,
  previewFile: File,
): Promise<string> {
  const apiBase = resolveFilesApiAddress(filesApiAddress);
  const token = getFilesApiAuthToken();
  const formData = new FormData();
  formData.append("file", previewFile, previewFile.name);

  try {
    const response = await axios.post(
      `${apiBase}/api/Files/UploadScreenshot`,
      formData,
      {
        headers: {
          authorization: "Bearer " + token,
        },
      },
    );
    const url = readScreenshotUrl(response.data);
    if (url) {
      return url;
    }
  } catch {
    // Older Cloud Files API builds do not have UploadScreenshot yet.
  }

  return uploadScreenshotViaBase64(apiBase, token, previewFile);
}

/**
 * Creates a PDF/image thumbnail in the browser and stores it. Returns "" when
 * the file type cannot be previewed or upload fails — never throws.
 */
export async function createAndUploadFilePreview(
  filesApiAddress: string,
  file: File,
): Promise<string> {
  try {
    const previewFile = await createFilePreviewFile(file);
    if (!previewFile) {
      return "";
    }
    return await uploadClientScreenshot(filesApiAddress, previewFile);
  } catch {
    return "";
  }
}
